import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  XMarkIcon, 
  PlusIcon, 
  TrashIcon, 
  CalendarDaysIcon, 
  ClockIcon,
  UserGroupIcon,
  CheckIcon,
  FireIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const WorkoutPlanForm = ({ plan, onClose, onSave, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    difficulty: 'intermediate',
    duration: 4,
    sessionsPerWeek: 3,
    category: 'musculation',
    exercises: [],
    targetMuscles: [],
    estimatedCalories: 300,
    notes: ''
  });

  const [selectedExercises, setSelectedExercises] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    if (mode === 'edit' && plan) {
      setFormData(plan);
      setSelectedExercises(plan.exercises || []);
    }
  }, [mode, plan]);

  const difficultyOptions = [
    { value: 'beginner', label: 'Débutant', color: 'text-green-400', icon: '🟢' },
    { value: 'intermediate', label: 'Intermédiaire', color: 'text-yellow-400', icon: '🟡' },
    { value: 'advanced', label: 'Avancé', color: 'text-red-400', icon: '🔴' }
  ];

  const categoryOptions = [
    { value: 'musculation', label: 'Musculation', icon: '💪' },
    { value: 'cardio', label: 'Cardio', icon: '❤️' },
    { value: 'renforcement', label: 'Renforcement', icon: '🏋️' },
    { value: 'etirement', label: 'Étirement', icon: '🧘' },
    { value: 'fonctionnel', label: 'Fonctionnel', icon: '⚡' }
  ];

  const muscleGroups = [
    'Pectoraux', 'Dos', 'Épaules', 'Biceps', 'Triceps', 'Avant-bras',
    'Quadriceps', 'Ischio-jambiers', 'Mollets', 'Fessiers', 'Abdominaux', 'Lombaires'
  ];

  const mockExercises = [
    { id: 1, name: 'Développé couché', category: 'musculation', muscles: ['Pectoraux', 'Triceps'], duration: 20, calories: 80 },
    { id: 2, name: 'Squat', category: 'musculation', muscles: ['Quadriceps', 'Fessiers'], duration: 15, calories: 60 },
    { id: 3, name: 'Tractions', category: 'musculation', muscles: ['Dos', 'Biceps'], duration: 10, calories: 50 },
    { id: 4, name: 'Course sur tapis', category: 'cardio', muscles: ['Cardio'], duration: 30, calories: 200 },
    { id: 5, name: 'Burpees', category: 'fonctionnel', muscles: ['Corps entier'], duration: 10, calories: 100 },
    { id: 6, name: 'Planche', category: 'renforcement', muscles: ['Abdominaux', 'Épaules'], duration: 5, calories: 30 },
    { id: 7, name: 'Étirement dos', category: 'etirement', muscles: ['Dos'], duration: 10, calories: 15 },
    { id: 8, name: 'Pompes', category: 'musculation', muscles: ['Pectoraux', 'Triceps'], duration: 10, calories: 40 }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExerciseSelection = (exercise) => {
    setSelectedExercises(prev => {
      const isSelected = prev.find(e => e.id === exercise.id);
      if (isSelected) {
        return prev.filter(e => e.id !== exercise.id);
      } else {
        return [...prev, { ...exercise, sets: 3, reps: 10, rest: 60 }];
      }
    });
  };

  const updateExerciseConfig = (exerciseId, field, value) => {
    setSelectedExercises(prev => 
      prev.map(ex => 
        ex.id === exerciseId ? { ...ex, [field]: parseInt(value) } : ex
      )
    );
  };

  const removeExercise = (exerciseId) => {
    setSelectedExercises(prev => prev.filter(ex => ex.id !== exerciseId));
  };

  const calculateTotalDuration = () => {
    return selectedExercises.reduce((total, ex) => {
      return total + ex.duration + (ex.sets * ex.rest / 60);
    }, 0);
  };

  const calculateTotalCalories = () => {
    return selectedExercises.reduce((total, ex) => {
      return total + ex.calories;
    }, 0);
  };

  const handleSubmit = () => {
    const planData = {
      ...formData,
      exercises: selectedExercises,
      totalDuration: calculateTotalDuration(),
      totalCalories: calculateTotalCalories(),
      targetMuscles: [...new Set(selectedExercises.flatMap(ex => ex.muscles))]
    };

    onSave(planData);
  };

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-white/20">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {mode === 'edit' ? 'Modifier' : 'Créer'} un plan d'entraînement
            </h2>
            <div className="flex items-center gap-4 mt-2">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step <= currentStep ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/60'
                  }`}>
                    {step}
                  </div>
                  <span className={`text-sm ${step <= currentStep ? 'text-white' : 'text-white/60'}`}>
                    {step === 1 ? 'Informations' : step === 2 ? 'Exercices' : 'Révision'}
                  </span>
                  {step < 3 && <div className="w-8 h-px bg-white/20" />}
                </div>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Étape 1: Informations générales */}
          {currentStep === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Nom du plan
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Programme perte de poids"
                  />
                </div>

                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Catégorie
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categoryOptions.map(cat => (
                      <option key={cat.value} value={cat.value} className="bg-gray-800">
                        {cat.icon} {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Niveau de difficulté
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => handleInputChange('difficulty', e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {difficultyOptions.map(diff => (
                      <option key={diff.value} value={diff.value} className="bg-gray-800">
                        {diff.icon} {diff.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Durée (semaines)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Séances par semaine
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="7"
                    value={formData.sessionsPerWeek}
                    onChange={(e) => handleInputChange('sessionsPerWeek', parseInt(e.target.value))}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Décrivez les objectifs et spécificités de ce plan..."
                />
              </div>
            </motion.div>
          )}

          {/* Étape 2: Sélection des exercices */}
          {currentStep === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Liste des exercices disponibles */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Exercices disponibles</h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {mockExercises.map(exercise => (
                      <div
                        key={exercise.id}
                        onClick={() => handleExerciseSelection(exercise)}
                        className={`p-4 rounded-xl cursor-pointer transition-all ${
                          selectedExercises.find(e => e.id === exercise.id)
                            ? 'bg-blue-500/20 border-blue-500/50'
                            : 'bg-white/10 border-white/20 hover:bg-white/15'
                        } border`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-white">{exercise.name}</h4>
                            <p className="text-white/60 text-sm">
                              {exercise.muscles.join(', ')} • {exercise.duration}min • {exercise.calories} cal
                            </p>
                          </div>
                          {selectedExercises.find(e => e.id === exercise.id) && (
                            <CheckIcon className="h-5 w-5 text-blue-400" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Exercices sélectionnés */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Exercices sélectionnés ({selectedExercises.length})
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedExercises.map(exercise => (
                      <div
                        key={exercise.id}
                        className="p-4 bg-white/10 rounded-xl border border-white/20"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-white">{exercise.name}</h4>
                          <button
                            onClick={() => removeExercise(exercise.id)}
                            className="p-1 text-red-400 hover:text-red-300 transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-white/60 text-xs mb-1">Séries</label>
                            <input
                              type="number"
                              min="1"
                              max="10"
                              value={exercise.sets}
                              onChange={(e) => updateExerciseConfig(exercise.id, 'sets', e.target.value)}
                              className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-white/60 text-xs mb-1">Reps</label>
                            <input
                              type="number"
                              min="1"
                              max="50"
                              value={exercise.reps}
                              onChange={(e) => updateExerciseConfig(exercise.id, 'reps', e.target.value)}
                              className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-white/60 text-xs mb-1">Repos (s)</label>
                            <input
                              type="number"
                              min="30"
                              max="300"
                              value={exercise.rest}
                              onChange={(e) => updateExerciseConfig(exercise.id, 'rest', e.target.value)}
                              className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Étape 3: Révision */}
          {currentStep === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <ClockIcon className="h-5 w-5 text-blue-400" />
                    <span className="text-white/80">Durée totale</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {Math.round(calculateTotalDuration())} min
                  </div>
                </div>

                <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <FireIcon className="h-5 w-5 text-orange-400" />
                    <span className="text-white/80">Calories</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {calculateTotalCalories()} cal
                  </div>
                </div>

                <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <ChartBarIcon className="h-5 w-5 text-purple-400" />
                    <span className="text-white/80">Exercices</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {selectedExercises.length}
                  </div>
                </div>
              </div>

              <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">Résumé du plan</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-white/60">Nom:</span>
                    <span className="text-white ml-2">{formData.name}</span>
                  </div>
                  <div>
                    <span className="text-white/60">Catégorie:</span>
                    <span className="text-white ml-2">{categoryOptions.find(c => c.value === formData.category)?.label}</span>
                  </div>
                  <div>
                    <span className="text-white/60">Difficulté:</span>
                    <span className="text-white ml-2">{difficultyOptions.find(d => d.value === formData.difficulty)?.label}</span>
                  </div>
                  <div>
                    <span className="text-white/60">Durée:</span>
                    <span className="text-white ml-2">{formData.duration} semaines</span>
                  </div>
                  <div>
                    <span className="text-white/60">Séances/semaine:</span>
                    <span className="text-white ml-2">{formData.sessionsPerWeek}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-white/20">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="px-6 py-3 bg-white/10 text-white rounded-xl border border-white/20 hover:bg-white/15 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Précédent
            </button>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-white/10 text-white rounded-xl border border-white/20 hover:bg-white/15 transition-colors"
              >
                Annuler
              </button>
              
              {currentStep < 3 ? (
                <button
                  onClick={nextStep}
                  disabled={currentStep === 1 && !formData.name}
                  className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={selectedExercises.length === 0}
                  className="px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mode === 'edit' ? 'Modifier' : 'Créer'} le plan
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WorkoutPlanForm; 